/**
 * HTML Sanitization Utilities
 * 
 * Provides secure HTML handling to prevent XSS attacks.
 * Uses DOM parsing for reliable sanitization.
 */

/**
 * Sanitize HTML to prevent XSS attacks - only allow safe formatting tags
 * Removes script tags, style tags, iframes, event handlers, and javascript: URLs
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  // Create a temporary element to parse the HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove all script tags and other dangerous elements
  const dangerousElements = doc.querySelectorAll('script, style, iframe, embed, object, link, meta, base');
  dangerousElements.forEach(el => el.remove());

  // Remove event handlers and dangerous attributes from all elements
  const allElements = doc.querySelectorAll('*');
  allElements.forEach(el => {
    // Get all attributes to check
    const attrs = Array.from(el.attributes);
    attrs.forEach(attr => {
      const attrName = attr.name.toLowerCase();
      const attrValue = attr.value.toLowerCase();

      // Remove all on* attributes (onclick, onerror, onload, etc.)
      if (attrName.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }

      // Remove javascript: URLs from href, src, etc.
      if (['href', 'src', 'action', 'formaction', 'data', 'poster', 'background'].includes(attrName)) {
        if (attrValue.includes('javascript:') || attrValue.includes('data:text/html') || attrValue.includes('vbscript:')) {
          el.removeAttribute(attr.name);
        }
      }

      // Remove dangerous CSS expressions
      if (attrName === 'style') {
        if (attrValue.includes('expression(') || attrValue.includes('url(') || attrValue.includes('javascript:')) {
          el.removeAttribute(attr.name);
        }
      }
    });
  });

  // Get the sanitized HTML from body
  return doc.body.innerHTML;
};

/**
 * Strip all HTML tags and return plain text
 * Security: Uses DOM parser instead of fragile regex for reliable HTML stripping
 * First sanitizes the HTML to remove dangerous elements, then extracts text content
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // First sanitize to remove dangerous elements, then extract text
  const doc = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
  return doc.body.textContent || '';
};
